type CbarSelectElement = HTMLElement & {
  items: Array<{ id: string; title: string; disabled?: boolean }>;
  value: string;
  updateComplete?: Promise<boolean>;
  _internals?: ElementInternals;
};

type MdSelectElement = HTMLElement & {
  value?: string;
  select?: (value: string) => void;
  updateComplete?: Promise<boolean>;
};

const CBAR_SELECT_HYDRATED_ATTR = 'data-cbar-select-hydrated';
const CBAR_SELECT_SUBMIT_NAME_ATTR = 'data-cbar-select-submit-name';
const CBAR_SELECT_HIDDEN_ATTR = 'data-cbar-select-bridge';
const CBAR_SELECT_TAGS = 'cbar-select, cbar-select2, cbar-entity-select';

const patchedSubmitForms = new WeakSet<HTMLFormElement>();

type CbarMultiSelectElement = HTMLElement & {
  items: Array<{ id: string; value: string }>;
  value: Array<{ id: string; value: string } | string>;
};

const pendingAppends: WeakMap<Element, Array<{ id: string; title?: string; value?: string }>> = new WeakMap();

function isCbarSelect(el: Element | null | undefined): el is CbarSelectElement {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'CBAR-SELECT' || tag === 'CBAR-SELECT2' || tag === 'CBAR-ENTITY-SELECT';
}

function isCbarMultiSelect(el: Element | null | undefined): el is CbarMultiSelectElement {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'CBAR-MULTI-SELECT-COMBO-BOX' || tag === 'CBAR-MULTI-SELECT';
}

function flushPendingAppends(el: Element): void {
  const queue = pendingAppends.get(el);
  if (!queue) {
    return;
  }
  pendingAppends.delete(el);

  if (!isCbarSelect(el) && !isCbarMultiSelect(el)) {
    return;
  }

  const currentItems = (el.items ?? []) as Array<{ id: string; title?: string; value?: string }>;
  // @ts-ignore
    el.items = [...currentItems, ...queue];
  if (window.CBAR?.sizeFormSelects) {
    window.CBAR.sizeFormSelects(el);
  }
}

function queueAppend(el: Element, item: { id: string; title?: string; value?: string }): void {
  let queue = pendingAppends.get(el);
  if (!queue) {
    queue = [];
    pendingAppends.set(el, queue);
    queueMicrotask(() => flushPendingAppends(el));
  }
  queue.push(item);
}

function selectedLabelsForMulti(el: CbarMultiSelectElement): string[] {
  const selected = el.value;
  if (!Array.isArray(selected)) {
    return [];
  }

  return selected.map((entry) => {
    if (entry && typeof entry === 'object' && 'value' in entry) {
      return String(entry.value);
    }
    const match = el.items?.find((item) => String(item.id) === String(entry));
    return match ? String(match.value) : String(entry);
  });
}

export function setupCbarSelectJqueryBridge(): void {
  const jq = (window as Window & { // @ts-ignore
      jQuery?: JQueryStatic }).jQuery;
  if (!jq) {
    return;
  }

  const origVal = jq.fn.val;
  // @ts-ignore
    jq.fn.val = function (this: JQuery, value?: unknown) {
    if (this.length > 0) {
      for (let i = 0; i < this.length; i++) {
        const el = this[i];
        if (isCbarSelect(el)) {
          if (value === undefined) {
            if (this.length === 1) {
              return resolveCbarSelectValue(el);
            }
            continue;
          }
          if (value !== undefined) {
            const newVal = Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
            if (el.value !== newVal) {
              flushPendingAppends(el);
              el.value = newVal;
              el.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else if (isCbarMultiSelect(el)) {
          if (value === undefined) {
            if (this.length === 1) {
              return el.value.map((entry) =>
                entry && typeof entry === 'object' && 'id' in entry ? String(entry.id) : String(entry)
              );
            }
            continue;
          }
          if (value !== undefined) {
            flushPendingAppends(el);
            const values = (Array.isArray(value) ? value : [value]).filter(
              (entry) => entry !== undefined && entry !== null && entry !== ''
            );
            const newValues = values.map((entry) => {
              const id = String(entry);
              const item = el.items?.find((candidate) => String(candidate.id) === id);
              return item ?? { id, value: id };
            });
            // Simple equality check for multi-select value
            const currentIds = el.value.map((v) => (typeof v === 'object' ? v.id : v));
            const newIds = newValues.map((v) => v.id);
            if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
              el.value = newValues;
              el.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else {
          // For non-custom elements in a mixed set, we use the original val()
          // but we have to do it carefully. To keep it simple, we'll let the
          // final origVal.apply handle it if we haven't returned yet.
        }
      }
      if (value !== undefined) {
        return this;
      }
    }
    return origVal.apply(this, arguments as unknown as [unknown?]);
  };

  const origEmpty = jq.fn.empty;
  // @ts-ignore
    jq.fn.empty = function (this: JQuery) {
    if (this.length > 0) {
      for (let i = 0; i < this.length; i++) {
        const el = this[i];
        if (isCbarSelect(el)) {
          flushPendingAppends(el);
          el.items = [];
          el.value = '';
        } else if (isCbarMultiSelect(el)) {
          flushPendingAppends(el);
          el.items = [];
          el.value = [];
        }
      }
      return this;
    }
    return origEmpty.apply(this, arguments as unknown as []);
  };

  const origAppend = jq.fn.append;
  // @ts-ignore
    jq.fn.append = function (this: JQuery, ...args: unknown[]) {
    let customFound = false;
    if (this.length > 0) {
      for (let i = 0; i < this.length; i++) {
        const el = this[i];
        if (isCbarSelect(el) || isCbarMultiSelect(el)) {
          customFound = true;
          for (const arg of args) {
            const optionEl =
              jq.isFunction(arg) || arg === undefined
                ? null
                : arg instanceof Element
                  ? arg
                  : arg && typeof arg === 'object' && 'length' in arg && (arg as { [index: number]: unknown })[0] instanceof Element
                    ? (arg as { [index: number]: Element })[0]
                    : jq(arg as string).length
                      ? jq(arg as string)[0]
                      : null;
            if (!optionEl || optionEl.tagName !== 'OPTION') {
              // If it's not an OPTION, we might still want to append it physically?
              // But custom selects don't usually take physical children other than for initialization.
              // For now, let's call the original append for these cases just in case.
              // @ts-ignore
                origAppend.apply(jq(el), [arg as JQuery.AcceptData]);
              continue;
            }
            const id = optionEl.getAttribute('value') ?? '';
            const title = optionEl.textContent ?? '';
            if (isCbarSelect(el)) {
              queueAppend(el, { id, title });
            } else {
              queueAppend(el, { id, value: title });
            }
          }
        }
      }
    }
    
    if (!customFound) {
      // @ts-ignore
        return origAppend.apply(this, args as [JQuery.AcceptData]);
    }
    return this;
  };
}

type CbarMultiSelectItem = {
  id: string;
  value: string;
};

function normalizeMultiSelectEntries(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeMultiSelectItems(raw: unknown): CbarMultiSelectItem[] {
  return normalizeMultiSelectEntries(raw)
    .map((entry) => {
      if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;
        return {
          id: String(record.id ?? ''),
          value: String(record.value ?? record.title ?? record.id ?? ''),
        };
      }
      const text = String(entry);
      return {id: text, value: text};
    })
    .filter((item) => item.id !== '' || item.value !== '');
}

function extractMultiSelectEntryId(
  entry: unknown,
  searchProp: string,
  items: CbarMultiSelectItem[],
): string {
  if (entry == null || entry === '') {
    return '';
  }
  if (typeof entry === 'string' || typeof entry === 'number') {
    const asString = String(entry);
    if (asString.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(asString) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object') {
          return extractMultiSelectEntryId(parsed, searchProp, items);
        }
      } catch {
        // fall through
      }
    }
    const byId = items.find((item) => String(item.id) === asString);
    if (byId) {
      return String(byId.id);
    }
    const byLabel = items.find((item) => String(item.value) === asString);
    return byLabel ? String(byLabel.id) : asString;
  }
  if (typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    // Prefer stable item id for form submission; search-prop is for dropdown matching only.
    if (record.id != null && record.id !== '') {
      return String(record.id);
    }
    const searchValue = searchProp ? record[searchProp] : undefined;
    if (searchValue != null && searchValue !== '') {
      const bySearchProp = items.find((item) => {
        const itemRecord = item as Record<string, unknown>;
        return String(itemRecord[searchProp] ?? '') === String(searchValue);
      });
      if (bySearchProp) {
        return String(bySearchProp.id);
      }
      const byId = items.find((item) => String(item.id) === String(searchValue));
      if (byId) {
        return String(byId.id);
      }
      const byLabel = items.find((item) => String(item.value) === String(searchValue));
      if (byLabel) {
        return String(byLabel.id);
      }
      return String(searchValue);
    }
    const label = record.value ?? record.title;
    if (label != null && label !== '') {
      const match = items.find((item) => String(item.value) === String(label));
      return match ? String(match.id) : String(label);
    }
  }
  return '';
}

function readMultiSelectIdsFromChips(
  el: CbarMultiSelectElement,
  items: CbarMultiSelectItem[],
): string[] {
  const root = el.shadowRoot;
  if (!root) {
    return [];
  }
  const displayField = el.getAttribute('display-field') ?? 'value';
  const chipLabels = Array.from(root.querySelectorAll('#tokens cbar-chip'))
    .map((chip) => chip.textContent?.trim() ?? '')
    .filter((label) => label.length > 0);

  return chipLabels.flatMap((label) => {
    const match = items.find((item) => {
      const itemRecord = item as Record<string, unknown>;
      const fieldValue = itemRecord[displayField];
      return String(fieldValue ?? item.value) === label;
    });
    return match ? [String(match.id)] : [];
  });
}

function getMultiSelectIdsViaJquery(id: string): string[] {
  const jq = (window as Window & {jQuery?: JQueryStatic}).jQuery;
  if (!jq) {
    return [];
  }
  const val = jq(`#${CSS.escape(id)}`).val();
  if (Array.isArray(val)) {
    return val.map(String).filter(Boolean);
  }
  return val != null && val !== '' ? [String(val)] : [];
}

/** Selected item ids from a cbar multi-select (property, chips, or jQuery bridge). */
export function getCbarMultiSelectIdsFromElement(el: CbarMultiSelectElement): string[] {
  const items = normalizeMultiSelectItems(el.items);
  const searchProp = el.getAttribute('search-prop') ?? 'id';
  const entries = normalizeMultiSelectEntries(el.value);
  let ids = entries
    .map((entry) => extractMultiSelectEntryId(entry, searchProp, items))
    .filter((value) => value.length > 0);

  if (ids.length === 0) {
    ids = readMultiSelectIdsFromChips(el, items);
  }

  if (ids.length === 0 && el.id) {
    ids = getMultiSelectIdsViaJquery(el.id);
  }

  return [...new Set(ids)];
}

/** Selected item ids from a cbar multi-select (property, chips, or jQuery bridge). */
export function getCbarMultiSelectIds(id: string): string[] {
  const el = document.getElementById(id);
  if (!el || !isCbarMultiSelect(el)) {
    return getMultiSelectIdsViaJquery(id);
  }

  return getCbarMultiSelectIdsFromElement(el);
}

export function getCbarSelectSelectedItem(
  selector: string
): { id?: string; title?: string; disabled?: boolean; [key: string]: unknown } | undefined {
  const el = document.querySelector(selector);
  if (isCbarSelect(el)) {
    return el.items?.find((item) => String(item.id) === String(el.value));
  }
  return undefined;
}

export function getCbarSelectSelectedText(selector: string): string {
  const el = document.querySelector(selector);
  if (isCbarSelect(el)) {
    const match = el.items?.find((item) => String(item.id) === String(el.value));
    return match?.title ?? el.value;
  }
  if (isCbarMultiSelect(el)) {
    return selectedLabelsForMulti(el).join(', ');
  }
  const jq = (window as Window & { // @ts-ignore
      jQuery?: JQueryStatic }).jQuery;
  return jq ? String(jq(selector).find(':selected').text()) : '';
}

export function setCbarSelectItems(
  target: string | Element,
  entities: Array<{ id: string | number; title: string }>,
  allOptionText = ''
): void {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!isCbarSelect(el)) {
    return;
  }

  const items = entities.map((entity) => ({
    id: String(entity.id),
    title: entity.title,
  }));

  if (allOptionText) {
    items.unshift({ id: 'all', title: allOptionText });
  }

  el.items = [...items];
  applyDefaultCbarSelectValue(el);
}

export function parseItemsFromAttribute(el: CbarSelectElement): Array<{ id: string; title: string; disabled?: boolean }> {
  const raw = el.getAttribute('items')?.trim() ?? '';
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((entry) => {
        if (typeof entry !== 'object' || entry === null) {
          return null;
        }
        const record = entry as Record<string, unknown>;
        const id = String(record.id ?? '');
        if (id === '') {
          return null;
        }
        return {
          id,
          title: String(record.title ?? record.label ?? record.value ?? id),
          disabled: Boolean(record.disabled),
        };
      })
      .filter((item): item is { id: string; title: string; disabled?: boolean } => item !== null);
  } catch {
    return [];
  }
}

function selectItems(el: CbarSelectElement): Array<{ id: string; title: string; disabled?: boolean }> {
  if (Array.isArray(el.items) && el.items.length > 0) {
    return el.items;
  }

  return parseItemsFromAttribute(el);
}

function firstEnabledSelectItemId(
  items: Array<{ id: string; title: string; disabled?: boolean }>,
): string {
  const firstItem = items.find((item) => !item.disabled) ?? items[0];
  return firstItem ? String(firstItem.id) : '';
}

function entitySelectAllowsCustomValue(el: CbarSelectElement): boolean {
  return el.tagName === 'CBAR-ENTITY-SELECT' && el.hasAttribute('allow-custom-value');
}

/** Resolved value from property, server-rendered attribute, or first enabled item. */
export function resolveCbarSelectValue(el: CbarSelectElement | null | undefined): string {
  if (!el) {
    return '';
  }

  const current = el.value?.trim() ?? '';
  if (current) {
    return current;
  }

  if (!el.hasAttribute(CBAR_SELECT_HYDRATED_ATTR)) {
    const attrValue = el.getAttribute('value')?.trim() ?? '';
    if (attrValue) {
      return attrValue;
    }
  }

  if (entitySelectAllowsCustomValue(el)) {
    return '';
  }

  return firstEnabledSelectItemId(selectItems(el));
}

function getCbarSelectDropdown(el: CbarSelectElement): MdSelectElement | null {
  return el.shadowRoot?.querySelector('md-filled-select, md-outlined-select') as MdSelectElement | null;
}

function syncCbarSelectDropdown(el: CbarSelectElement, value = resolveCbarSelectValue(el)): void {
  const resolved = value.trim();
  if (!resolved) {
    return;
  }

  const dropdown = getCbarSelectDropdown(el);
  if (!dropdown) {
    return;
  }

  if (dropdown.value !== resolved) {
    dropdown.value = resolved;
  }

  dropdown.select?.(resolved);
}

function valueMatchesSelectItem(
  value: string,
  items: Array<{ id: string; title: string; disabled?: boolean }>,
): boolean {
  return items.some((item) => String(item.id) === value && !item.disabled);
}

function syncCbarSelectElementValidity(el: CbarSelectElement, value: string): void {
  const internals = el._internals;
  if (!internals) {
    return;
  }

  const dropdown = getCbarSelectDropdown(el);
  if (dropdown && typeof dropdown.checkValidity === 'function' && dropdown.checkValidity()) {
    internals.setValidity(dropdown.validity, dropdown.validationMessage, dropdown);
    return;
  }

  if (value && valueMatchesSelectItem(value, selectItems(el))) {
    internals.setValidity({}, '', el);
  }
}

function hydrateCbarSelectItemsFromAttribute(el: CbarSelectElement): void {
  if (Array.isArray(el.items) && el.items.length > 0) {
    return;
  }

  const parsed = parseItemsFromAttribute(el);
  if (parsed.length > 0) {
    el.items = parsed;
  }
}

export function syncCbarSelectFormValue(el: CbarSelectElement): void {
  hydrateCbarSelectItemsFromAttribute(el);

  const value = resolveCbarSelectValue(el);
  syncCbarSelectDropdown(el, value);

  if (value && el.value !== value) {
    el.value = value;
  }

  const internals = el._internals;
  if (internals) {
    internals.setFormValue(value || null);
  }

  syncCbarSelectDropdown(el, value);
  syncCbarSelectElementValidity(el, value);
}

export function applyDefaultCbarSelectValue(el: CbarSelectElement): void {
  const resolved = resolveCbarSelectValue(el);
  if (!resolved) {
    return;
  }

  if (el.value !== resolved) {
    el.value = resolved;
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncCbarSelectFormValue(el);
}

/**
 * Copy server-rendered value/items attributes into cbar-select properties and
 * form-associated submission state after the custom element has upgraded.
 */
export async function hydrateCbarSelect(el: CbarSelectElement): Promise<void> {
  if (el.hasAttribute(CBAR_SELECT_HYDRATED_ATTR)) {
    syncCbarSelectFormValue(el);
    return;
  }

  await el.updateComplete;

  hydrateCbarSelectItemsFromAttribute(el);
  applyDefaultCbarSelectValue(el);
  await el.updateComplete;
  syncCbarSelectDropdown(el);

  const dropdown = el.shadowRoot?.querySelector('md-filled-select, md-outlined-select') as MdSelectElement | null;
  await dropdown?.updateComplete;
  syncCbarSelectDropdown(el);
  syncCbarSelectFormValue(el);

  el.setAttribute(CBAR_SELECT_HYDRATED_ATTR, '');
}

export function hydrateCbarSelectsIn(root: ParentNode = document): void {
  root.querySelectorAll(CBAR_SELECT_TAGS).forEach((node) => {
    if (isCbarSelect(node)) {
      void hydrateCbarSelect(node);
    }
  });
}

export function defaultEmptyCbarSelectValues(root: ParentNode = document): void {
  root.querySelectorAll(CBAR_SELECT_TAGS).forEach((node) => {
    if (isCbarSelect(node)) {
      applyDefaultCbarSelectValue(node);
    }
  });
}

const SUBMIT_CONTROL_TAGS = new Set(['BUTTON', 'INPUT', 'CBAR-BUTTON']);

function isFormSubmitControl(el: EventTarget): el is HTMLElement {
  if (!isElementLike(el)) {
    return false;
  }

  if (!SUBMIT_CONTROL_TAGS.has(el.tagName)) {
    return false;
  }

  const type = el.getAttribute('type')?.toLowerCase() ?? '';

  if (el.tagName === 'INPUT') {
    return type === 'submit';
  }

  if (el.tagName === 'BUTTON' || el.tagName === 'CBAR-BUTTON') {
    return type === '' || type === 'submit';
  }

  return false;
}

function isHtmlFormElement(form: unknown): form is HTMLFormElement {
  return typeof form === 'object'
    && form !== null
    && 'querySelectorAll' in form
    && typeof (form as HTMLFormElement).querySelectorAll === 'function';
}

function getFormForSubmitControl(control: HTMLElement): HTMLFormElement | null {
  if (control.tagName === 'BUTTON' || control.tagName === 'INPUT') {
    const nativeControl = control as HTMLButtonElement | HTMLInputElement;
    if (isHtmlFormElement(nativeControl.form)) {
      return nativeControl.form;
    }
  }

  const associated = (control as HTMLElement & { internals?: ElementInternals }).internals;
  if (isHtmlFormElement(associated?.form)) {
    return associated.form;
  }

  if (typeof control.closest === 'function') {
    const closestForm = control.closest('form');
    return isHtmlFormElement(closestForm) ? closestForm : null;
  }

  return null;
}

function isElementLike(node: EventTarget): node is HTMLElement {
  return typeof node === 'object'
    && node !== null
    && 'tagName' in node
    && typeof (node as HTMLElement).getAttribute === 'function';
}

function findSubmitControlFromEvent(event: Event): HTMLElement | null {
  for (const node of event.composedPath()) {
    if (isElementLike(node) && isFormSubmitControl(node)) {
      return node;
    }
  }

  return null;
}

export function syncCbarSelectsInForm(form: ParentNode): void {
  form.querySelectorAll(CBAR_SELECT_TAGS).forEach((node) => {
    if (isCbarSelect(node)) {
      syncCbarSelectFormValue(node);
    }
  });
}

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function hiddenInputsForCbarSelectField(form: HTMLFormElement, name: string): HTMLInputElement[] {
  const selector = `input[type="hidden"][${CBAR_SELECT_HIDDEN_ATTR}="${escapeSelectorValue(name)}"]`;
  return Array.from(form.querySelectorAll(selector)) as HTMLInputElement[];
}

function clearCbarSelectFormAssociation(el: CbarSelectElement): void {
  el.removeAttribute('name');
  el.name = '';
  el._internals?.setFormValue(null);
}

function restoreCbarSelectFormAssociation(el: CbarSelectElement, name: string): void {
  el.setAttribute('name', name);
  el.name = name;
  syncCbarSelectFormValue(el);
}

/**
 * Swap cbar-select form participation for native hidden inputs so HTML5
 * validation does not consult lagging md-select shadow state on submit.
 */
export function applyCbarSelectSubmitValues(form: HTMLFormElement): void {
  form.querySelectorAll(CBAR_SELECT_TAGS).forEach((node) => {
    if (!isCbarSelect(node)) {
      return;
    }

    syncCbarSelectFormValue(node);

    const name = node.getAttribute('name') ?? node.name;
    if (!name) {
      return;
    }

    const value = resolveCbarSelectValue(node);
    hiddenInputsForCbarSelectField(form, name).forEach((hidden) => {
      hidden.parentNode?.removeChild(hidden);
    });

    if (value) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = name;
      hidden.value = value;
      hidden.setAttribute(CBAR_SELECT_HIDDEN_ATTR, name);
      form.appendChild(hidden);
    }

    if (!node.hasAttribute(CBAR_SELECT_SUBMIT_NAME_ATTR)) {
      node.setAttribute(CBAR_SELECT_SUBMIT_NAME_ATTR, name);
      clearCbarSelectFormAssociation(node);
    }
  });
}

export function restoreCbarSelectNames(form: HTMLFormElement): void {
  form.querySelectorAll(`[${CBAR_SELECT_SUBMIT_NAME_ATTR}]`).forEach((node) => {
    if (!isCbarSelect(node)) {
      return;
    }

    const name = node.getAttribute(CBAR_SELECT_SUBMIT_NAME_ATTR);
    if (!name) {
      return;
    }

    restoreCbarSelectFormAssociation(node, name);
    node.removeAttribute(CBAR_SELECT_SUBMIT_NAME_ATTR);
  });

  form.querySelectorAll(`input[type="hidden"][${CBAR_SELECT_HIDDEN_ATTR}]`).forEach((node) => {
    node.parentNode?.removeChild(node);
  });
}

export function patchFormCbarSelectSubmit(form: HTMLFormElement): void {
  if (patchedSubmitForms.has(form)) {
    return;
  }

  patchedSubmitForms.add(form);

  const originalRequestSubmit = form.requestSubmit.bind(form);
  form.requestSubmit = function requestSubmitWithCbarSelectBridge(submitter?: HTMLElement): void {
    syncCbarSelectsInForm(form);
    applyCbarSelectSubmitValues(form);
    try {
      originalRequestSubmit(submitter);
    } finally {
      restoreCbarSelectNames(form);
    }
  };
}

function patchFormsWithCbarSelects(root: ParentNode = document): void {
  if (root instanceof HTMLFormElement && root.querySelector(CBAR_SELECT_TAGS)) {
    patchFormCbarSelectSubmit(root);
  }

  if ('querySelectorAll' in root) {
    root.querySelectorAll('form').forEach((form) => {
      if (form.querySelector(CBAR_SELECT_TAGS)) {
        patchFormCbarSelectSubmit(form);
      }
    });
  }
}

export function setupCbarSelectSubmitSync(): void {
  const prepareFormForSubmit = (form: HTMLFormElement): void => {
    patchFormCbarSelectSubmit(form);
    syncCbarSelectsInForm(form);
    applyCbarSelectSubmitValues(form);
  };

  const syncBeforeSubmit = (event: Event) => {
    const submitControl = findSubmitControlFromEvent(event);
    if (!submitControl) {
      return;
    }

    const form = getFormForSubmitControl(submitControl);
    if (!form) {
      return;
    }

    prepareFormForSubmit(form);
  };

  document.addEventListener('click', syncBeforeSubmit, true);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement) {
        restoreCbarSelectNames(form);
      }
    },
    true,
  );
}

export async function setupCbarSelectHydration(): Promise<void> {
  await Promise.all([
    customElements.whenDefined('cbar-select'),
    customElements.whenDefined('cbar-select2'),
    customElements.whenDefined('cbar-entity-select'),
  ]);

  const run = () => {
    hydrateCbarSelectsIn(document);
    patchFormsWithCbarSelects(document);
  };
  run();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (isCbarSelect(node)) {
          void hydrateCbarSelect(node);
          patchFormsWithCbarSelects(node);
          return;
        }

        hydrateCbarSelectsIn(node);
        patchFormsWithCbarSelects(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

export function setCbarMultiSelectItems(
  target: string | Element,
  entities: Array<{ id: string | number; title: string }>
): void {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!isCbarMultiSelect(el)) {
    return;
  }

  el.items = entities.map((entity) => ({
    id: String(entity.id),
    value: entity.title,
  }));
}

export { isCbarMultiSelect, isCbarSelect, selectedLabelsForMulti };
